import { now } from '@eturi/date-util'
import { resetAction } from '@op/services'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'
import identity from 'lodash/identity.js'
import { createSliceTransformer } from 'rtk-slice-transformer'

export type UserFastPollExpiryMap = {
	readonly [userId: string]: number
}

export type FastPollState = {
	readonly blockStatus: UserFastPollExpiryMap
	readonly devices: number
	readonly location: number
	readonly users: number
}

export type WithFastPollState = {
	readonly fastPoll: FastPollState
}

const initialState: FastPollState = {
	blockStatus: {},
	devices: 0,
	location: 0,
	users: 0,
}

export const fastPollSlice = createSlice({
	name: 'fastPoll',
	initialState,
	reducers: {
		cancelFastPollDevices(s) {
			s.devices = 0
		},

		cancelFastPollLocation(s) {
			s.location = 0
		},

		cancelFastPollUsers(s) {
			s.users = 0
		},

		fastPollDevices(s, a: PayloadAction<number | undefined>) {
			s.devices = a.payload ?? defaultExpiry()
		},

		fastPollLocation(s, a: PayloadAction<number | undefined>) {
			s.location = a.payload ?? defaultExpiry()
		},

		fastPollUserBlockStatus(s, a: PayloadAction<string>) {
			s.blockStatus[a.payload] = defaultExpiry()
		},

		fastPollUsers(s, a: PayloadAction<number | undefined>) {
			s.users = a.payload ?? defaultExpiry()
		},
	},

	extraReducers: (builder) => builder.addCase(resetAction, () => initialState),
})

const defaultExpiry = () => +now.add(2, 'm')

export const {
	cancelFastPollDevices,
	cancelFastPollUsers,
	cancelFastPollLocation,
	fastPollDevices,
	fastPollLocation,
	fastPollUserBlockStatus,
	fastPollUsers,
} = fastPollSlice.actions

const isPollingAction = /*@__PURE__*/ isAnyOf(...(Object.values(fastPollSlice.actions) as any))

export const fastPollSliceTransformer = /*@__PURE__*/ createSliceTransformer(
	fastPollSlice,
	identity,
	(a) => (isPollingAction(a) ? null : a),
)

// Selectors

const state$ = <T extends WithFastPollState>(s: T) => s.fastPoll

export const devicesFastPollExpiry$ = /*@__PURE__*/ createSelector(state$, (s) => s.devices)
export const locationFastPollExpiry$ = /*@__PURE__*/ createSelector(state$, (s) => s.location)
export const userBlockStatusFastPollExpiryMap$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.blockStatus,
)
export const usersFastPollExpiry$ = /*@__PURE__*/ createSelector(state$, (s) => s.users)
